import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  clone,
  flattenFields,
  getWidget,
  keyFromLabel,
  labelOf,
  localize,
  primaryType,
  schemaAtPath,
  uniqueKey,
  type FormLocale,
  type I18nText,
  type LogicField,
  type PropertySchema,
  type UiField,
  type UiSection,
  type UiStep,
} from "@ncp/forms";
import type { FormBody } from "./api";

/** What is selected in the design canvas. */
export type Selection = { kind: "field"; path: string } | { kind: "section"; step: string; section: string } | { kind: "step"; step: string } | null;

// ---------------------------------------------------------------- lookup helpers

export function findStep(body: FormBody, stepKey: string): UiStep | undefined {
  return body.uiSchema.steps.find((s) => s.key === stepKey);
}

export function findSection(body: FormBody, stepKey: string, sectionKey: string): UiSection | undefined {
  return findStep(body, stepKey)?.sections.find((s) => s.key === sectionKey);
}

/** Where a field is placed (first placement wins). */
export function placementOf(body: FormBody, path: string): { step: UiStep; section: UiSection; field: UiField; index: number } | undefined {
  for (const step of body.uiSchema.steps) {
    for (const section of step.sections) {
      const index = section.fields.findIndex((f) => f.path === path);
      if (index >= 0) return { step, section, field: section.fields[index]!, index };
    }
  }
  return undefined;
}

/** Parent object schema and the property key for a (possibly nested) path. */
function parentOf(schema: PropertySchema, path: string): { parent: PropertySchema; key: string } | undefined {
  const segs = path.split(".");
  const key = segs.pop()!;
  let parent: PropertySchema | undefined = schema;
  for (const seg of segs) {
    parent = parent?.properties?.[seg];
    if (parent && !parent.properties && parent.items?.properties) parent = parent.items;
    if (!parent) return undefined;
  }
  return parent ? { parent, key } : undefined;
}

/** Fields the LogicBuilder can reference, with option labels for enums and `date` typing from `format`. */
export function logicFields(body: FormBody, locale: FormLocale): LogicField[] {
  return flattenFields(body.schema, locale).map((f) => {
    const t = f.type;
    const type: LogicField["type"] = t === "string" && (f.schema.format === "date" || f.schema.format === "date-time") ? "date" : t;
    const enumValues = f.schema.enum ?? f.schema.items?.enum;
    const opts = enumValues?.map((v) => ({ value: String(v), label: localize(f.schema["x-ncp"]?.enumOptions?.find((o) => String(o.value) === String(v))?.label, locale) || String(v) }));
    return { path: f.path, label: f.label, type, options: opts };
  });
}

// ---------------------------------------------------------------- mutations (all return a new body)

function update(body: FormBody, fn: (draft: FormBody) => void): FormBody {
  const draft = clone(body);
  fn(draft);
  return draft;
}

export function addStep(body: FormBody, title: I18nText, review = false): { body: FormBody; key: string } {
  const key = uniqueKey(keyFromLabel(localize(title, "en") || "step"), body.uiSchema.steps.map((s) => s.key));
  return {
    key,
    body: update(body, (d) => {
      d.uiSchema.steps.push({ key, title, sections: review ? [] : [{ key: "main", columns: 2, fields: [] }], ...(review ? { review: true } : {}) });
    }),
  };
}

export function updateStep(body: FormBody, stepKey: string, patch: Partial<UiStep>): FormBody {
  return update(body, (d) => {
    const s = d.uiSchema.steps.find((x) => x.key === stepKey);
    if (s) Object.assign(s, patch);
  });
}

export function removeStep(body: FormBody, stepKey: string, deleteFields: boolean): FormBody {
  return update(body, (d) => {
    const idx = d.uiSchema.steps.findIndex((x) => x.key === stepKey);
    if (idx < 0) return;
    const [step] = d.uiSchema.steps.splice(idx, 1);
    if (deleteFields && step) for (const s of step.sections) for (const f of s.fields) if (f.path) deleteSchemaProperty(d.schema, f.path);
  });
}

export function moveStep(body: FormBody, stepKey: string, direction: -1 | 1): FormBody {
  return update(body, (d) => {
    const i = d.uiSchema.steps.findIndex((x) => x.key === stepKey);
    const j = i + direction;
    if (i < 0 || j < 0 || j >= d.uiSchema.steps.length) return;
    [d.uiSchema.steps[i], d.uiSchema.steps[j]] = [d.uiSchema.steps[j]!, d.uiSchema.steps[i]!];
  });
}

export function addSection(body: FormBody, stepKey: string, title?: I18nText): { body: FormBody; key: string } {
  const step = findStep(body, stepKey);
  const key = uniqueKey(keyFromLabel(localize(title, "en") || "section"), step?.sections.map((s) => s.key) ?? []);
  return { key, body: update(body, (d) => { d.uiSchema.steps.find((x) => x.key === stepKey)?.sections.push({ key, title, columns: 2, fields: [] }); }) };
}

export function updateSection(body: FormBody, stepKey: string, sectionKey: string, patch: Partial<UiSection>): FormBody {
  return update(body, (d) => {
    const s = d.uiSchema.steps.find((x) => x.key === stepKey)?.sections.find((x) => x.key === sectionKey);
    if (s) Object.assign(s, patch);
  });
}

export function removeSection(body: FormBody, stepKey: string, sectionKey: string, deleteFields: boolean): FormBody {
  return update(body, (d) => {
    const step = d.uiSchema.steps.find((x) => x.key === stepKey);
    if (!step) return;
    const idx = step.sections.findIndex((x) => x.key === sectionKey);
    if (idx < 0) return;
    const [section] = step.sections.splice(idx, 1);
    if (deleteFields && section) for (const f of section.fields) if (f.path) deleteSchemaProperty(d.schema, f.path);
  });
}

export function moveSection(body: FormBody, stepKey: string, sectionKey: string, direction: -1 | 1): FormBody {
  return update(body, (d) => {
    const step = d.uiSchema.steps.find((x) => x.key === stepKey);
    if (!step) return;
    const i = step.sections.findIndex((x) => x.key === sectionKey);
    const j = i + direction;
    if (i < 0 || j < 0 || j >= step.sections.length) return;
    [step.sections[i], step.sections[j]] = [step.sections[j]!, step.sections[i]!];
  });
}

/** Creates a schema property from a palette widget and places it at the end of a section (or at `index`). */
export function addField(body: FormBody, stepKey: string, sectionKey: string, widgetKey: string, locale: FormLocale, index?: number): { body: FormBody; path: string } {
  const widget = getWidget(widgetKey);
  const base = widget ? localize(widget.label, "en") : widgetKey;
  const key = uniqueKey(keyFromLabel(base), Object.keys(body.schema.properties ?? {}));
  const schema: PropertySchema = widget ? widget.defaultSchema() : { type: "string" };
  const ext = { ...(schema["x-ncp"] ?? {}) };
  if (widget && widget.key !== "text" && widget.key !== "number" && !ext.widget) ext.widget = widget.key;
  ext.title = widget ? { en: localize(widget.label, "en"), ar: localize(widget.label, "ar") } : { en: base };
  schema["x-ncp"] = ext;
  void locale;
  return {
    path: key,
    body: update(body, (d) => {
      d.schema.properties = { ...(d.schema.properties ?? {}), [key]: schema };
      const section = d.uiSchema.steps.find((x) => x.key === stepKey)?.sections.find((x) => x.key === sectionKey);
      if (!section) return;
      const placement: UiField = { path: key, ...(widget?.defaultSpan ? { span: widget.defaultSpan } : {}) };
      if (index === undefined || index >= section.fields.length) section.fields.push(placement);
      else section.fields.splice(Math.max(0, index), 0, placement);
    }),
  };
}

/** Adds a static content block (markdown) to a section. */
export function addContent(body: FormBody, stepKey: string, sectionKey: string, content: I18nText, index?: number): FormBody {
  return update(body, (d) => {
    const section = d.uiSchema.steps.find((x) => x.key === stepKey)?.sections.find((x) => x.key === sectionKey);
    if (!section) return;
    const placement: UiField = { path: "", content, span: 12 };
    if (index === undefined || index >= section.fields.length) section.fields.push(placement);
    else section.fields.splice(Math.max(0, index), 0, placement);
  });
}

/** Removes a placement; when `deleteSchema` the property (and its rules/bindings) go too. */
export function removeField(body: FormBody, stepKey: string, sectionKey: string, index: number, deleteSchema: boolean): FormBody {
  return update(body, (d) => {
    const section = d.uiSchema.steps.find((x) => x.key === stepKey)?.sections.find((x) => x.key === sectionKey);
    if (!section) return;
    const [f] = section.fields.splice(index, 1);
    if (deleteSchema && f?.path) {
      deleteSchemaProperty(d.schema, f.path);
      d.rules = d.rules.filter((r) => !(r.fields ?? []).includes(f.path));
      delete d.bindings[f.path];
    }
  });
}

/** Moves a placement inside or across sections. */
export function moveField(body: FormBody, from: { step: string; section: string; index: number }, to: { step: string; section: string; index: number }): FormBody {
  return update(body, (d) => {
    const src = d.uiSchema.steps.find((x) => x.key === from.step)?.sections.find((x) => x.key === from.section);
    const dst = d.uiSchema.steps.find((x) => x.key === to.step)?.sections.find((x) => x.key === to.section);
    if (!src || !dst) return;
    const [f] = src.fields.splice(from.index, 1);
    if (!f) return;
    let idx = to.index;
    if (src === dst && from.index < idx) idx -= 1;
    dst.fields.splice(Math.min(Math.max(0, idx), dst.fields.length), 0, f);
  });
}

export function updateUiField(body: FormBody, path: string, patch: Partial<UiField>): FormBody {
  return update(body, (d) => {
    for (const step of d.uiSchema.steps) for (const section of step.sections) for (const f of section.fields) if (f.path === path) Object.assign(f, patch);
  });
}

/** Patches the property schema at `path`. Undefined values delete the key. */
export function updateFieldSchema(body: FormBody, path: string, patch: Partial<PropertySchema>): FormBody {
  return update(body, (d) => {
    const hit = parentOf(d.schema, path);
    if (!hit) return;
    const prop = hit.parent.properties?.[hit.key];
    if (!prop) return;
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined) delete (prop as Record<string, unknown>)[k];
      else (prop as Record<string, unknown>)[k] = v;
    }
  });
}

/** Patches `x-ncp` on the property. */
export function updateFieldExt(body: FormBody, path: string, patch: Partial<NonNullable<PropertySchema["x-ncp"]>>): FormBody {
  return update(body, (d) => {
    const hit = parentOf(d.schema, path);
    const prop = hit?.parent.properties?.[hit.key];
    if (!prop) return;
    const ext = { ...(prop["x-ncp"] ?? {}) } as Record<string, unknown>;
    for (const [k, v] of Object.entries(patch)) {
      if (v === undefined || v === "" || v === null) delete ext[k];
      else ext[k] = v;
    }
    prop["x-ncp"] = ext as PropertySchema["x-ncp"];
  });
}

export function setRequired(body: FormBody, path: string, required: boolean): FormBody {
  return update(body, (d) => {
    const hit = parentOf(d.schema, path);
    if (!hit) return;
    const list = new Set(hit.parent.required ?? []);
    if (required) list.add(hit.key); else list.delete(hit.key);
    hit.parent.required = [...list];
  });
}

export function isRequired(body: FormBody, path: string): boolean {
  const hit = parentOf(body.schema, path);
  return !!hit && (hit.parent.required ?? []).includes(hit.key);
}

/** Renames a top-level or nested property and rewrites every reference (placements, rules, bindings, conditions). */
export function renameField(body: FormBody, path: string, newKey: string): { body: FormBody; path: string } | { error: string } {
  const clean = newKey.trim();
  if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(clean)) return { error: "invalidKey" };
  const hit = parentOf(body.schema, path);
  if (!hit) return { error: "notFound" };
  if (hit.key === clean) return { body, path };
  if (hit.parent.properties?.[clean]) return { error: "duplicateKey" };
  const segs = path.split(".");
  segs.pop();
  const newPath = [...segs, clean].join(".");
  const next = update(body, (d) => {
    const h = parentOf(d.schema, path)!;
    const props = h.parent.properties!;
    const rebuilt: Record<string, PropertySchema> = {};
    for (const [k, v] of Object.entries(props)) rebuilt[k === h.key ? clean : k] = v;
    h.parent.properties = rebuilt;
    if (h.parent.required) h.parent.required = h.parent.required.map((k) => (k === h.key ? clean : k));
    // references
    const text = JSON.stringify(d.uiSchema).split(`"${path}"`).join(`"${newPath}"`).split(`"${path}.`).join(`"${newPath}.`);
    d.uiSchema = JSON.parse(text);
    d.rules = JSON.parse(JSON.stringify(d.rules).split(`"${path}"`).join(`"${newPath}"`).split(`"${path}.`).join(`"${newPath}.`));
    const bindings: Record<string, string> = {};
    for (const [k, v] of Object.entries(d.bindings)) bindings[k === path ? newPath : k.startsWith(`${path}.`) ? newPath + k.slice(path.length) : k] = v;
    d.bindings = bindings;
    if (d.sampleData && segs.length === 0 && Object.prototype.hasOwnProperty.call(d.sampleData, path)) {
      const { [path]: v, ...rest } = d.sampleData;
      d.sampleData = { ...rest, [clean]: v };
    }
  });
  return { body: next, path: newPath };
}

/** Duplicates a field (schema + placement right after the original). */
export function duplicateField(body: FormBody, path: string): { body: FormBody; path: string } | undefined {
  const placement = placementOf(body, path);
  const prop = schemaAtPath(body.schema, path);
  if (!placement || !prop || path.includes(".")) return undefined;
  const key = uniqueKey(path, Object.keys(body.schema.properties ?? {}));
  const next = update(body, (d) => {
    d.schema.properties![key] = clone(prop);
    const section = d.uiSchema.steps.find((s) => s.key === placement.step.key)!.sections.find((s) => s.key === placement.section.key)!;
    section.fields.splice(placement.index + 1, 0, { ...clone(placement.field), path: key });
  });
  return { body: next, path: key };
}

function deleteSchemaProperty(schema: PropertySchema, path: string): void {
  const hit = parentOf(schema, path);
  if (!hit?.parent.properties) return;
  delete hit.parent.properties[hit.key];
  if (hit.parent.required) hit.parent.required = hit.parent.required.filter((k) => k !== hit.key);
}

/** Unplaced schema properties (exist in the schema but are not in any step). */
export function unplaced(body: FormBody): string[] {
  const placed = new Set(body.uiSchema.steps.flatMap((s) => s.sections.flatMap((x) => x.fields.map((f) => f.path))));
  return Object.keys(body.schema.properties ?? {}).filter((k) => !placed.has(k));
}

/** Placements whose schema property no longer exists. */
export function orphans(body: FormBody): string[] {
  const out: string[] = [];
  for (const step of body.uiSchema.steps) for (const section of step.sections) for (const f of section.fields) if (f.path && !schemaAtPath(body.schema, f.path)) out.push(f.path);
  return out;
}

export function fieldLabel(body: FormBody, path: string, locale: FormLocale): string {
  return labelOf(schemaAtPath(body.schema, path), path, locale);
}

export function fieldType(body: FormBody, path: string) {
  return primaryType(schemaAtPath(body.schema, path));
}

// ---------------------------------------------------------------- history (undo / redo)

export interface History<T> {
  present: T;
  canUndo: boolean;
  canRedo: boolean;
  set: (next: T | ((prev: T) => T), opts?: { transient?: boolean }) => void;
  undo: () => void;
  redo: () => void;
  reset: (value: T) => void;
}

/**
 * Undo/redo stack for the builder body. `transient` updates (typing in a label) replace the last entry when it was also transient
 * within a short window, so a keystroke is not one undo step.
 */
export function useHistory<T>(initial: T, limit = 100): History<T> {
  const [state, setState] = useState<{ past: T[]; present: T; future: T[] }>({ past: [], present: initial, future: [] });
  const lastTransient = useRef(0);
  const set = useCallback((next: T | ((prev: T) => T), opts?: { transient?: boolean }) => {
    setState((s) => {
      const value = typeof next === "function" ? (next as (p: T) => T)(s.present) : next;
      if (value === s.present) return s;
      const now = Date.now();
      const merge = opts?.transient && now - lastTransient.current < 800;
      lastTransient.current = opts?.transient ? now : 0;
      const past = merge ? s.past : [...s.past, s.present].slice(-limit);
      return { past, present: value, future: [] };
    });
  }, [limit]);
  const undo = useCallback(() => setState((s) => (s.past.length ? { past: s.past.slice(0, -1), present: s.past[s.past.length - 1]!, future: [s.present, ...s.future] } : s)), []);
  const redo = useCallback(() => setState((s) => (s.future.length ? { past: [...s.past, s.present], present: s.future[0]!, future: s.future.slice(1) } : s)), []);
  const reset = useCallback((value: T) => setState({ past: [], present: value, future: [] }), []);
  return useMemo(() => ({ present: state.present, canUndo: state.past.length > 0, canRedo: state.future.length > 0, set, undo, redo, reset }), [state, set, undo, redo, reset]);
}

/** Cmd/Ctrl+Z / Shift+Cmd/Ctrl+Z while the builder is mounted (ignored inside inputs and CodeMirror, which have their own history). */
export function useUndoShortcuts(h: Pick<History<unknown>, "undo" | "redo">) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== "z") return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable || target.closest(".cm-editor"))) return;
      e.preventDefault();
      if (e.shiftKey) h.redo(); else h.undo();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [h]);
}

export function stableJson(v: unknown): string {
  return JSON.stringify(v ?? null);
}
